import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ScaleLoader } from 'react-spinners';
import session from 'redux-persist/es/storage/session';
import { useTheme } from 'styled-components';
import { CoveringLoader } from '../../../components/LoadingSpinner/CoveringLoader';
import { IPlanDownloadRequest } from '../../../logic/Models/Requests/PlanRequest';
import { UserPermission } from '../../../logic/Models/Responses/UserPermissions';
import { convertProductPlanToFieldPlan } from '../../../logic/store/Plans/FieldPlan/FieldPlanThunks';
import { createProductGamePlan } from '../../../logic/store/Plans/ProductGamePlanActions';
import { selectedEditingProductPlan } from '../../../logic/store/Plans/ProductGamePlanSlice';
import { saveProductPlan } from '../../../logic/store/Plans/ProductGamePlanUpdateActions';
import { newPlanDummyId } from '../../../logic/store/Plans/ProductPlanUtils';
import { useSeedsForSelectedGrower } from '../../../logic/store/Seeds/useSeedsForSelectedGrower';
import { RootState, useAppDispatch, useAppSelector } from '../../../logic/store/Store';
import { setSelectedPlanId } from '../../../logic/store/UI/UISlice';
import { hasEffectivePermission } from '../../../logic/store/User/AuthSlice';
import { useAmplitudePassthrough } from '../../../logic/Utility/useAmplitude';
import { EventAcreProposalNames } from '../../../tracing/EventNames';
import { EventStructureTags } from '../../../tracing/EventTagNames';
import { useScopedSession } from '../../../tracing/session';
import { cropBrandAssociations, ICropBrandAssociation } from '../ProductGamePlan/CropBrandAssociation';
import { CropTabBar } from '../ProductGamePlan/CropTabBar';
import { ConvertModal } from '../ProductGamePlan/ProductGamePlan';
import { ProductPlanHeaderRow } from '../ProductGamePlan/ProductPlanHeaderRow';
import { useProductGamePlanTrackingState } from '../ProductGamePlan/useProductGamePlanTrackingState';

type Props = {
	style?: React.CSSProperties;
}

export const ProductGamePlanV7 = ({
	style,
}: Props) => 
{
	const seeds = useSeedsForSelectedGrower();
	const theme = useTheme();
	const history = useHistory();	
	const dispatch = useAppDispatch();
	
	const planId = useAppSelector(s => s.productGamePlan.selectedGamePlanId);
	const isSavingProductPlan = useAppSelector(s => s.productGamePlan.isSavingProductPlan);
	const isDownloadingProductPlan = useAppSelector(s => s.productGamePlan.isDownloadingProductPlan);
	const isLoadingCreateProductPlan = useAppSelector(s => s.productGamePlan.isLoadingCreateProductPlan);

	const userCanSeeEnogenBrand =  useAppSelector(s => hasEffectivePermission(s, UserPermission.CanSeeEnogenBrand));
	const [cropBrand, setCropBrand] = useState<ICropBrandAssociation>(cropBrandAssociations[0]); // currentRecommendation
	
	const localEditingState = useAppSelector(selectedEditingProductPlan); // TODO: This needs a v7 version.
	const growers = useAppSelector(s => s.grower.Growers);
	const selectedGrowerId = useAppSelector(s => s.ui.SelectedGrowerId);
	const selectedGrower = useMemo(() => growers.find(g => g.Id === selectedGrowerId), [growers, selectedGrowerId]);

	const [isConvertingModalOpen, setConvertingModalOpen] = useState(false);

	const trackingData = useProductGamePlanTrackingState();
	const session = useScopedSession(planId, trackingData, {
		[EventStructureTags.PageContext]: 'acre_plan',
		[EventStructureTags.PageUrl]: window.location.toString()
	});

	const onSave = useAmplitudePassthrough(session, EventAcreProposalNames.SaveAcreProposal, async () =>
	{
		const pdfConfig: IPlanDownloadRequest = {
			PlanId: planId,
			CoverConfig: null
		};
		if (planId !== newPlanDummyId)
		{
			await dispatch(saveProductPlan({ request: pdfConfig, printPDF: false }));
		}
		else
		{
			await dispatch(createProductGamePlan({ coverConfig: pdfConfig, printPDF: false }));
		}
	}, [planId]);

	/** The action when a user confirms that they want to convert.  Will save the current plan and then convert it to a product plan. */
	const convert = useAmplitudePassthrough(session, EventAcreProposalNames.ConvertAcreProposalToFieldPlan, async () => 
	{
		const pdfConfig: IPlanDownloadRequest = {
			PlanId: planId,
			CoverConfig: null
		};
				
		let effectivePlanId = planId;
		if (planId !== newPlanDummyId)
		{
			await dispatch(saveProductPlan({ request: pdfConfig, printPDF: false }));
		}
		else
		{
			// Grab the new ID from the response
			effectivePlanId = await dispatch(createProductGamePlan({ coverConfig: pdfConfig, printPDF: false })).unwrap();
		}
		
		const response = await dispatch(convertProductPlanToFieldPlan({ productPlanId: effectivePlanId })).unwrap();
		const newPlanId = response?.Id;
		if(newPlanId)
		{
			// Errors are already handled by the thunk rejecting
			dispatch(setSelectedPlanId(newPlanId));
			history.push('/fieldactivities/fieldplan');
		}
	}, [planId, setSelectedPlanId, history, ]);
		

	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				...style,
			}}
		>
			<ProductPlanHeaderRow 
				planId={planId}
				isDownloadingProductPlan={isDownloadingProductPlan}
				isSavingPlan={isSavingProductPlan || isLoadingCreateProductPlan}
				onSave={onSave}
				onConvert={() => setConvertingModalOpen(true)}
				localEditState={localEditingState}
				selectedGrower={selectedGrower}
			/>
			<div className='ProductPlanRecTabs' style={{
				display: 'flex',
				flexDirection: 'row',
				borderBottom: `1px solid ${theme.colors.lightGrey}`,
				paddingLeft: 18
			}}>
				{cropBrandAssociations
					.filter(cba => userCanSeeEnogenBrand ? true : cba.brandApplication !== 'Enogen')
					.map(cba => (
						<CropTabBar
							key={cba.cropId + cba.brandApplication}
							label={cba.title}
							tabWidth={'95px'}
							selected={cba.cropId === cropBrand.cropId &&
						cba.brandApplication == cropBrand.brandApplication}
							onClick={() => setCropBrand({...cba})} 
						/>
					))}
			</div>
			{localEditingState && (
				<div className='ProductPlanEditorContent' style={{
					display: 'flex',
					flexDirection: 'row',
					height: '100%',
					flex: '1 1 auto',
					overflow: 'hidden'
				}}>
					<ConvertModal
						open={isConvertingModalOpen}
						onCancel={() => setConvertingModalOpen(false)}
						onConvert={convert} 
					/>
				</div>
			)}
			<CoveringLoader
				className={seeds.isLoading ? 'loading' : ''}
				style={{backgroundColor: '#ddda', display: 'flex', flexDirection: 'column'}}
			>
				<ScaleLoader color={theme.colors.ghxOrangeLM} loading={true} />
				Loading Seeds
			</CoveringLoader>
		</div>
	);
};